/**
 * Price Manager Store (Zustand)
 *
 * Reactive state wrapper around the priceManager singleton.
 * Provides React hooks for components to subscribe to price updates.
 *
 * All price logic is delegated to lib/priceManager.ts singleton.
 * This store just handles reactive state for UI components.
 */

import { create } from "zustand";
import { CardPrices } from "@/models/tcgplayer-prices";
import { SupportedGames } from "@/types/game";
import { priceManager } from "@/lib/priceManager";
import { createInitialStoreState } from "@/games";

interface PriceManagerState {
  // Keyed by every registered game rather than a hand-written list, so a new
  // game cannot be silently missing from price state.
  prices: Partial<Record<SupportedGames, CardPrices>>;
  loading: Record<SupportedGames, boolean>;
  fetchPrices: (game: SupportedGames) => Promise<void>;
  getCardPrice: (game: SupportedGames, cardId: string) => number | null;
  getTCGPlayerProductId: (
    game: SupportedGames,
    cardId: string,
  ) => string | undefined;
}

export const usePriceManager = create<PriceManagerState>((set, get) => ({
  prices: {},
  loading: createInitialStoreState<boolean>(false),

  /**
   * Fetch prices using the singleton priceManager
   * Updates Zustand state for reactive UI updates
   */
  fetchPrices: async (game: SupportedGames) => {
    // Only fetch on client side
    if (typeof window === "undefined") {
      return;
    }

    const state = get();

    // Don't fetch if already loaded or currently loading
    if (state.prices[game] || state.loading[game]) {
      return;
    }

    // Set loading state
    set((state) => ({
      loading: { ...state.loading, [game]: true },
    }));

    try {
      // Use priceManager singleton for actual fetching
      const data: CardPrices = await priceManager.getPrices(game);

      set((state) => ({
        prices: { ...state.prices, [game]: data },
        loading: { ...state.loading, [game]: false },
      }));
    } catch (error) {
      console.error(`Error fetching ${game} card prices:`, error);
      set((state) => ({
        loading: { ...state.loading, [game]: false },
      }));
    }
  },

  /**
   * Get card price from store (for reactive components)
   * Delegates to priceManager for actual price lookup
   */
  getCardPrice: (game: SupportedGames, cardId: string): number | null => {
    const state = get();
    const gamePrices = state.prices[game];

    if (!gamePrices) {
      // Trigger fetch if not loaded yet (fire and forget)
      if (!state.loading[game]) {
        state.fetchPrices(game);
      }
      return null;
    }

    // Use priceManager to get product ID and look up price
    const productId = priceManager.getTCGPlayerProductId(game, cardId);
    if (productId && gamePrices[productId]) {
      return parseFloat(gamePrices[productId].l);
    }

    return null;
  },

  /**
   * Get TCGPlayer product ID (delegates to priceManager)
   */
  getTCGPlayerProductId: (
    game: SupportedGames,
    cardId: string,
  ): string | undefined => {
    return priceManager.getTCGPlayerProductId(game, cardId);
  },
}));

// Legacy export for backward compatibility
export const useCardPricesStore = usePriceManager;
